export const ORDER_PROGRESS_STEPS = ['Order Placed', 'Preparing', 'Ready to Serve'] as const;

export const ITEM_STATUS_LABELS: Record<string, string> = {
  PLACED: 'Order received',
  CONFIRMED: 'Confirmed',
  PREPARING: 'Being prepared',
  READY: 'Ready',
  SERVED: 'Served',
  COMPLETED: 'Complete',
  CANCELLED: 'Cancelled',
};

export interface OrderItemStatusLine {
  status?: string;
  itemName?: string;
  active?: boolean;
}

export function itemStatusToStep(status?: string): number {
  switch ((status || '').toUpperCase()) {
    case 'PREPARING':
      return 1;
    case 'READY':
      return 2;
    case 'SERVED':
    case 'COMPLETED':
      return 3;
    case 'CANCELLED':
      return 3;
    default:
      return 0;
  }
}

export function deriveOrderProgressFromItems(items: OrderItemStatusLine[]): number {
  if (!items || items.length === 0) {
    return 0;
  }
  const active = items.filter((i) => i.active !== false && i.status !== 'CANCELLED');
  if (active.length === 0) {
    return 3;
  }
  let min = 3;
  for (const item of active) {
    min = Math.min(min, itemStatusToStep(item.status));
  }
  return min;
}

export function itemStatusLabel(status?: string): string {
  if (!status) return ITEM_STATUS_LABELS.PLACED;
  return ITEM_STATUS_LABELS[status.toUpperCase()] || status;
}

/** CSS class for item status badge in order detail tables. */
export function itemStatusBadgeClass(status?: string): string {
  switch ((status || 'PLACED').toUpperCase()) {
    case 'PREPARING':
      return 'item-status-badge item-status-badge--preparing';
    case 'READY':
      return 'item-status-badge item-status-badge--ready';
    case 'SERVED':
    case 'COMPLETED':
      return 'item-status-badge item-status-badge--done';
    case 'CANCELLED':
      return 'item-status-badge item-status-badge--cancelled';
    case 'CONFIRMED':
    case 'PLACED':
    default:
      return 'item-status-badge item-status-badge--placed';
  }
}

/** e.g. "2 preparing, 1 ready" */
export function summarizeItemStatuses(items: OrderItemStatusLine[]): string {
  if (!items || items.length === 0) return '';
  const counts: Record<string, number> = {};
  for (const item of items) {
    if (item.active === false || item.status === 'CANCELLED') continue;
    const key = (item.status || 'PLACED').toUpperCase();
    counts[key] = (counts[key] || 0) + 1;
  }
  const parts = Object.entries(counts).map(([status, count]) =>
    `${count} ${itemStatusLabel(status).toLowerCase()}`
  );
  return parts.join(', ');
}

const FINISHED_KITCHEN_ORDER_STATUSES = new Set([
  'ORDER_PREPARED',
  'ORDER_PICKED',
  'ORDER_SERVED',
  'ORDER_COMPLETED',
  'DINEIN_ORDER_COMPLETED',
  'TAKEAWAY_ORDER_COMPLETED',
  'DELIVERY_ORDER_COMPLETED',
]);

/** Relative rank for comparing order-level vs item-derived guest status. */
function orderStatusRank(status: string): number {
  switch ((status || '').toUpperCase()) {
    case 'ORDER_COMPLETED':
    case 'DINEIN_ORDER_COMPLETED':
    case 'TAKEAWAY_ORDER_COMPLETED':
    case 'DELIVERY_ORDER_COMPLETED':
      return 60;
    case 'ORDER_SERVED':
      return 50;
    case 'ORDER_PREPARED':
    case 'ORDER_PICKED':
      return 40;
    case 'ORDER_PREPARING':
      return 30;
    case 'ORDER_CONFIRMED':
    case 'PAYMENT_DONE':
      return 20;
    case 'ORDER_PLACED':
    case 'ORDER_INITIATED':
    case 'PENDING_RESTAURANT_ACCEPTANCE':
      return 10;
    default:
      return 0;
  }
}

/**
 * Guest headline status: item-derived kitchen progress vs staff order-level updates
 * (e.g. ORDER_SERVED when lines are still READY). Prefer whichever is further along.
 */
export function resolveGuestOrderStatus(
  orderStatus: string | undefined,
  items: OrderItemStatusLine[]
): string {
  const fromOrder = (orderStatus || '').trim();
  const hasItemStatus = items.some((i) => i.status);
  const derived = hasItemStatus ? deriveOrderStatusFromItems(items, fromOrder) : null;

  if (!fromOrder && !derived) return '';
  if (!derived) return fromOrder;
  if (!fromOrder) return derived;

  return orderStatusRank(fromOrder) >= orderStatusRank(derived) ? fromOrder : derived;
}

/** Step index for guest progress UI; order-level status can advance past item-derived step. */
export function resolveGuestOrderStep(
  orderStatus: string | undefined,
  items: OrderItemStatusLine[]
): number {
  const effective = resolveGuestOrderStatus(orderStatus, items);
  const fromOrder = orderStatusToStep(effective);
  if (!items.some((i) => i.status)) {
    return fromOrder;
  }
  return Math.max(deriveOrderProgressFromItems(items), fromOrder);
}

export function deriveOrderStatusFromItems(
  items: OrderItemStatusLine[],
  previousOrderStatus?: string
): string | null {
  if (!items || items.length === 0) return null;
  const active = items.filter((i) => i.active !== false && i.status !== 'CANCELLED');
  if (active.length === 0) return null;
  const statuses = active.map((i) => (i.status || 'PLACED').toUpperCase());
  if (statuses.every((s) => s === 'SERVED' || s === 'COMPLETED')) {
    return 'ORDER_SERVED';
  }
  if (statuses.every((s) => s === 'READY' || s === 'SERVED' || s === 'COMPLETED')) {
    return 'ORDER_PREPARED';
  }
  if (statuses.some((s) => s === 'PREPARING')) {
    return 'ORDER_PREPARING';
  }
  const previous = (previousOrderStatus || '').toUpperCase();
  if (
    previous === 'DELIVERY_ASSIGNED' ||
    previous === 'DELIVERY_AT_RESTAURANT' ||
    previous === 'ORDER_PICKED' ||
    previous === 'DELIVERY_PICKED' ||
    previous === 'DELIVERY_STARTED' ||
    previous === 'DELIVERY_COMPLETED' ||
    previous === 'DELIVERY_ORDER_COMPLETED'
  ) {
    return previous;
  }
  const anyKitchenProgress = statuses.some((s) => s === 'READY' || s === 'SERVED');
  if (anyKitchenProgress) {
    if (FINISHED_KITCHEN_ORDER_STATUSES.has(previous)) {
      return 'ORDER_CONFIRMED';
    }
    return 'ORDER_PREPARING';
  }
  if (previous === 'ORDER_PREPARING') {
    return 'ORDER_PREPARING';
  }
  return null;
}

export function orderStatusToStep(status: string): number {
  switch (status) {
    case 'ORDER_PREPARING':
      return 1;
    case 'ORDER_PREPARED':
    case 'ORDER_PICKED':
      return 2;
    case 'ORDER_SERVED':
      return 3;
    case 'ORDER_COMPLETED':
    case 'DINEIN_ORDER_COMPLETED':
    case 'TAKEAWAY_ORDER_COMPLETED':
    case 'DELIVERY_ORDER_COMPLETED':
    case 'ORDER_CANCELLED':
    case 'ORDER_FAILED':
      return 3;
    default:
      return 0;
  }
}

export const COMPLETED_ORDER_STATUSES = [
  'ORDER_COMPLETED',
  'DINEIN_ORDER_COMPLETED',
  'TAKEAWAY_ORDER_COMPLETED',
  'DELIVERY_ORDER_COMPLETED',
] as const;

export function isCompletedOrderStatus(status: string): boolean {
  return COMPLETED_ORDER_STATUSES.includes(status as typeof COMPLETED_ORDER_STATUSES[number]);
}

export function isTerminalOrderStatus(status: string): boolean {
  const s = String(status || '').toUpperCase();
  return (
    isCompletedOrderStatus(s) ||
    s === 'ORDER_CANCELLED' ||
    s === 'ORDER_FAILED' ||
    s === 'DELIVERY_CANCELLED' ||
    s === 'DELIVERY_FAILED'
  );
}

/** Keep a realtime terminal status if a slower GET still has the previous kitchen state. */
export function keepFreshGuestOrderStatus(local?: string, incoming?: string): string {
  const current = String(local || '').trim();
  const next = String(incoming || '').trim();
  if (!next) return current;
  if (!current) return next;
  if (isTerminalOrderStatus(current) && !isTerminalOrderStatus(next)) {
    return current;
  }
  // Stale poll must not roll kitchen progress backward after a CometD advance
  // (e.g. ORDER_PREPARED → ORDER_PREPARING). New lines reopen at CONFIRMED/PLACED.
  const nextUpper = next.toUpperCase();
  if (
    orderStatusRank(current) > orderStatusRank(next) &&
    orderStatusRank(next) > 0 &&
    nextUpper !== 'ORDER_CONFIRMED' &&
    nextUpper !== 'ORDER_MODIFIED' &&
    nextUpper !== 'ORDER_PLACED' &&
    nextUpper !== 'ORDER_INITIATED' &&
    nextUpper !== 'PENDING_RESTAURANT_ACCEPTANCE'
  ) {
    return current;
  }
  return next;
}

/** Online payment did not complete — not an active in-progress order for the guest bar. */
export function isPaymentIncompleteStatus(status?: string): boolean {
  const s = String(status || '').toUpperCase();
  return s === 'PAYMENT_INITIATED' || s === 'PAYMENT_FAILED' || s === 'PAYMENT_CANCELLED';
}

const ORDER_STATUS_LABELS: Record<string, string> = {
  ORDER_DRAFT: 'Order Draft',
  ORDER_INITIATED: 'Order Initiated',
  ORDER_PLACED: 'Order Placed',
  PENDING_RESTAURANT_ACCEPTANCE: 'Waiting for restaurant',
  ORDER_CONFIRMED: 'Order Confirmed',
  ORDER_MODIFIED: 'Order Modified',
  ORDER_PREPARING: 'Order Preparing',
  ORDER_PREPARED: 'Order Prepared',
  ORDER_SERVED: 'Served',
  ORDER_PICKED: 'Order Picked',
  ORDER_COMPLETED: 'Order Completed',
  ORDER_CANCELLED: 'Order Cancelled',
  ORDER_FAILED: 'Order Failed',
  PAYMENT_INITIATED: 'Payment Initiated',
  PAYMENT_DONE: 'Paid',
  PAYMENT_FAILED: 'Payment Failed',
  PAYMENT_CANCELLED: 'Payment Cancelled',
  DELIVERY_INITIATED: 'Order confirmed',
  DELIVERY_ASSIGNED: 'Rider assigned',
  DELIVERY_AT_RESTAURANT: 'At the restaurant',
  DELIVERY_STARTED: 'Out for delivery',
  DELIVERY_PICKED: 'Order picked',
  DELIVERY_COMPLETED: 'Delivered',
  DELIVERY_ORDER_COMPLETED: 'Delivered',
  DELIVERY_CANCELLED: 'Delivery cancelled',
  DELIVERY_FAILED: 'Delivery failed',
  DELIVERY_MODIFIED: 'Delivery modified',
};

export function orderStatusLabel(status: string): string {
  const code = String(status || '').toUpperCase();
  if (code === 'PENDING_RESTAURANT_ACCEPTANCE') {
    return 'Waiting for restaurant';
  }
  if (code === 'ORDER_SERVED') {
    return 'Served';
  }
  if (
    code === 'ORDER_CANCELLED' ||
    code === 'ORDER_FAILED' ||
    code === 'DELIVERY_CANCELLED' ||
    code === 'DELIVERY_FAILED'
  ) {
    return ORDER_STATUS_LABELS[code];
  }
  if (ORDER_STATUS_LABELS[code] && (code.startsWith('DELIVERY_') || code.startsWith('PAYMENT_'))) {
    return ORDER_STATUS_LABELS[code];
  }
  const step = orderStatusToStep(code);
  if (step >= ORDER_PROGRESS_STEPS.length) {
    return 'Order Complete';
  }
  if (step > 0 || code === 'ORDER_PLACED' || code === 'ORDER_INITIATED' || !code) {
    return ORDER_PROGRESS_STEPS[step];
  }
  return ORDER_STATUS_LABELS[code] || code.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

export function orderStatusHint(status: string): string {
  switch (status) {
    case 'ORDER_INITIATED':
    case 'ORDER_PLACED':
    case 'PENDING_RESTAURANT_ACCEPTANCE':
      return 'The restaurant is confirming your order.';
    case 'ORDER_CONFIRMED':
      return 'Your order has been received by the kitchen.';
    case 'ORDER_PREPARING':
      return 'The chef is preparing your food.';
    case 'ORDER_PREPARED':
    case 'ORDER_PICKED':
      return 'Your order is ready!';
    case 'ORDER_SERVED':
      return 'All items have been served to your table.';
    default:
      return 'We are working on your order.';
  }
}
